import {
  IBookingSummaryData,
  IssuedStatusEnum,
  IGDSBooking,
  ITicketWithAmount,
  IIssuingLog,
  IBookingDetailLock,
  ITicketDetail,
  IssuingRule,
  IssuingRuleId,
  IDate,
  IDateRange,
  LanguageEnum,
  BookingTypeEnum,
  BookingStatusEnum,
  IExtraInfo,
  PlanSettingPrintSpec,
  PlanSettingUISetting,
  MogiriSettingSupplier,
  MogiriSettingPlan,
  MogiriSettingTicket,
} from './types';
import request from '@/utils/request';
import {
  randomString,
  stringHashCode,
  formatDate,
  sleep,
  dateToIDate,
} from '@/utils/index';

// request & response
export interface ISearchBookingsRequest {
  participantName?: string;
  purchaseDateRange?: IDateRange;
  participationDateRange?: IDateRange;
  statuses?: IssuedStatusEnum[];
  activityName?: string;
  planName?: string;
  supplierId?: string;
  bookingIdPrefix?: string;
  itemCount: number;
  page: number;
}

export interface ISearchBookingsResponse {
  totalCount: number;
  itemCount: number;
  page: number;
  bookings: IBookingSummaryData[];
}

export interface IGetBookingDetailRequest {
  bookingId: string;
  systemSource?: string;
}

export type GetBookingDetailResponseInnerMogiriSetting = {
  supplierSetting?: MogiriSettingSupplier;
  planSetting?: MogiriSettingPlan;
  ticketSettings?: { [key: string]: MogiriSettingTicket };
};

export interface IGetBookingDetailResponse {
  booking: IGDSBooking;
  issuedStatus: IssuedStatusEnum;
  bookingStatus: BookingStatusEnum;
  extraInfos: IExtraInfo[];
  issuingRules?: IssuingRule[];
  tickets: ITicketWithAmount[];
  issuingLogs?: IIssuingLog[];
  lock: IBookingDetailLock;
  printSpecs?: PlanSettingPrintSpec[];
  uiSetting?: PlanSettingUISetting;
  mogiriSetting?: GetBookingDetailResponseInnerMogiriSetting;
  version: string;
}

export interface ILockBookingRequest {
  bookingId: string;
  note?: string;
  bookingRecordVersion: string;
}

export interface IUnlockBookingRequest {
  bookingId: string;
  note?: string;
}

// mock
const useMock = process.env.VUE_APP_API_USE_MOCK === 'true';
const useMockWithCache = process.env.VUE_APP_API_USE_MOCK === 'true';

const bookingIdToHash = (bookingId: string) =>
  Math.abs(stringHashCode(bookingId));

const bookingIdHashToIssuedStatus = (hash: number) => {
  if (hash < 0) {
    hash = -hash;
  }
  if (hash % 3 === 0) {
    return IssuedStatusEnum.NOT_ISSUED;
  } else if (hash % 3 === 1) {
    return IssuedStatusEnum.PARTIAL;
  } else {
    return IssuedStatusEnum.ISSUED;
  }
};

export const defaultBookingSummaryData: IBookingSummaryData = {
  bookingId: 'DUMMY-YYYYMMDD-',
  activityId: 'activityId',
  planId: 'planId',
  activityTitle: 'activityTitle',
  planTitle: 'planTitle',
  participantFirstName: 'participantFirstName',
  participantLastName: 'participantLastName',
  agentName: 'agentName',
  issuedStatus: IssuedStatusEnum.NOT_ISSUED,
  supplierId: 'supplierId',
  supplierName: 'supplierName',
  systemSource: 'GDS_NEXT',
};

const mockBookingSummaryDataById = (id: number) => {
  const result = Object.assign({}, defaultBookingSummaryData);
  result.bookingId += randomString(4);
  const num = bookingIdToHash(result.bookingId);
  result.activityId += id;
  result.planId += id;
  result.activityTitle += id;
  result.planTitle += id;
  result.participantFirstName += id;
  result.participantLastName += id;
  result.agentName += id;
  result.issuedStatus = bookingIdHashToIssuedStatus(num);
  result.supplierId += id;
  result.supplierName += id;
  return result;
};

const mockBookingSummaryDataList = (totalCount: number) => {
  const bookings: IBookingSummaryData[] = [];
  for (let i = 0; i < totalCount; i++) {
    bookings.push(mockBookingSummaryDataById(i));
  }
  return bookings;
};

const mockBookingSummaryDataSize = 100;
const defaultMockBookingSummaries = mockBookingSummaryDataList(
  mockBookingSummaryDataSize
);

const mockBookingSummaryWithServerPagination = (
  req: ISearchBookingsRequest
) => {
  return (async (req: ISearchBookingsRequest) => {
    let items = defaultMockBookingSummaries;
    if (req.itemCount > 0 && req.page > 0) {
      items = items.slice(
        (req.page - 1) * req.itemCount,
        req.page * req.itemCount
      );
    }
    return {
      totalCount: defaultMockBookingSummaries.length,
      itemCount: req.itemCount,
      page: req.page,
      bookings: items,
    } as ISearchBookingsResponse;
  })(req);
};

let searchBookingResponseWithCache: ISearchBookingsResponse | undefined;

export const mockSearchBookingsResponseWithCache = (
  req: ISearchBookingsRequest
) => {
  if (useMockWithCache && searchBookingResponseWithCache) {
    return searchBookingResponseWithCache;
  }
  searchBookingResponseWithCache = mockSearchBookingsResponse(req);
  return searchBookingResponseWithCache;
};

export const mockSearchBookingsResponse = (req: ISearchBookingsRequest) =>
  ({
    totalCount: 100,
    itemCount: req.itemCount,
    page: req.page,
    bookings: mockBookingSummaryDataList(100),
  } as ISearchBookingsResponse);

export const bookingDetailResponseWithCache: {
  [bookingId: string]: IGetBookingDetailResponse;
} = {};

export const mockGetBookingDetailResponseWithCache = (
  req: IGetBookingDetailRequest
) => {
  if (useMockWithCache && bookingDetailResponseWithCache[req.bookingId]) {
    return bookingDetailResponseWithCache[req.bookingId];
  }
  const res = mockGetBookingDetailResponse(req);
  bookingDetailResponseWithCache[req.bookingId] = res;
  return res;
};

export const mockGetBookingDetailResponse = (req: IGetBookingDetailRequest) => {
  const num = bookingIdToHash(req.bookingId);
  const res = {
    booking: mockGDSBooking(num, req.bookingId),
    issuedStatus: bookingIdHashToIssuedStatus(num),
    bookingStatus: BookingStatusEnum.UNKNOWN,
    extraInfos: [],
    issuingRules: [],
    tickets: [],
    issuingLogs: [],
    lock: mockBookingDetailLock(req.bookingId),
    version: '0',
  } as IGetBookingDetailResponse;
  switch (res.issuedStatus as IssuedStatusEnum) {
    case IssuedStatusEnum.NOT_ISSUED:
      res.tickets = mockNotIssuedTickets(num, req.bookingId);
      if (num % 2 === 0) {
        res.issuingRules!.push({
          id: IssuingRuleId.CANNOT_PARTIALY_ISSUE,
        } as IssuingRule);
      }
      break;
    case IssuedStatusEnum.PARTIAL:
      res.tickets = mockPartialIssuedTickets(num, req.bookingId);
      break;
    case IssuedStatusEnum.ISSUED:
      res.tickets = mockAllIssuedTickets(num, req.bookingId);
      if (num % 2 === 0) {
        res.issuingRules!.push({
          id: IssuingRuleId.CANNOT_PARTIALY_ISSUE,
        } as IssuingRule);
      }
      break;
    default:
      break;
  }
  return res;
};

export const mockGDSBooking = (num: number, bookingId: string) => {
  const current = new Date();
  const expired = new Date(current);
  expired.setDate(current.getDate() + 1);
  return {
    bookingId: bookingId,
    bookingStatus: BookingStatusEnum.UNKNOWN,
    activityId: 'activityId' + num,
    activityTitle: 'activityTitle' + num,
    planId: 'planId' + num,
    planTitle: 'planTitle' + num,
    targetDate: dateToIDate(current),
    expirationDate: dateToIDate(expired),
    bookedTimestamp: 'bookedTimestamp' + num,
    planStartTimeId: 'planStartTimeId' + num,
    planStartTime: 'planStartTime' + num,
    otaId: 'otaId' + num,
    agentName: 'agentName' + num,
    participantLastName: 'Lastname',
    participantFirstName: 'Firstname',
    agentBookingId: 'agentBookingId' + num,
    supplierId: 'supplierId' + num,
    supplierName: 'supplierName' + num,
    bookingType:
      num % 3 === 0 ? BookingTypeEnum.BULK : BookingTypeEnum.EACH_TIME,
  } as IGDSBooking;
};

const mockNotIssuedTickets = (num: number, bookingId: string) => {
  const tickets = [
    mockNotIssuedTicket(num, `${bookingId}-1`),
    mockNotIssuedTicket(num, `${bookingId}-2`),
  ];
  return tickets;
};

const mockPartialIssuedTickets = (num: number, bookingId: string) => {
  const tickets = [
    mockIssuedTicket(num, `${bookingId}-1`),
    mockNotIssuedTicket(num, `${bookingId}-2`),
  ];
  return tickets;
};

const mockAllIssuedTickets = (num: number, bookingId: string) => {
  const tickets = [
    mockIssuedTicket(num, `${bookingId}-1`),
    mockIssuedTicket(num, `${bookingId}-2`),
  ];
  return tickets;
};

const mockIssuedTicket = (num: number, ticketId: string) => {
  const ticket: ITicketWithAmount = {
    ticketId: ticketId,
    unitId: `${ticketId}-${num % 4}`,
    title: `${ticketId}-${num % 4}`,
    issuableTickets: [],
    issuedTickets: [mockTicketDetail(true), mockTicketDetail()],
    unissuableTickets: [],
    issuingRules: [],
  };
  return ticket;
};

const mockNotIssuedTicket = (num: number, ticketId: string) => {
  const unissuable = num % 2 === 1;
  const unissuableTicketTargetId = unissuable ? 'dummyTargetTicketId' : '';
  const cannotPartiallyIssue = num % 4 === 1;
  const ticket: ITicketWithAmount = {
    ticketId: ticketId,
    unitId: `${ticketId}-${num % 4}`,
    title: `${ticketId}-${num % 4}`,
    issuableTickets: [mockTicketDetail(true), mockTicketDetail()],
    issuedTickets: [],
    unissuableTickets: unissuable
      ? [mockUnissuableTicketDetail(unissuableTicketTargetId)]
      : [],
    issuingRules: [],
  };
  if (unissuable) {
    ticket.issuingRules.push({
      id: IssuingRuleId.MUST_ISSUE_AFTER_SPECIFIC_TICKET,
      targetTicketId: unissuableTicketTargetId,
    });
  }
  if (cannotPartiallyIssue) {
    ticket.issuingRules.push({
      id: IssuingRuleId.CANNOT_PARTIALY_ISSUE,
    });
  }
  return ticket;
};

const mockTicketDetail = (halfYear: boolean = false) =>
  ({
    issuableFrom: new Date('2019-09-01').toISOString(),
    issuableTo: new Date(halfYear ? '2020-03-01' : '2020-09-01').toISOString(),
    amount: 2,
    disobeyedRules: [],
    noPermission: false,
  } as ITicketDetail);

const mockUnissuableTicketDetail = (
  targetTicketId: string,
  halfYear: boolean = false
) => {
  return {
    issuableFrom: new Date('2019-09-01').toISOString(),
    issuableTo: new Date(halfYear ? '2020-03-01' : '2020-09-01').toISOString(),
    amount: 1,
    disobeyedRules: [
      {
        id: IssuingRuleId.MUST_ISSUE_AFTER_SPECIFIC_TICKET,
        targetTicketId: targetTicketId,
      },
    ],
    noPermission: false,
  } as ITicketDetail;
};

const mockBookingDetailLock = (bookingId: string) => {
  return {
    isActive: false,
    accountId: undefined,
    accountName: undefined,
    note: undefined,
    updatedAt: undefined,
  } as IBookingDetailLock;
};

const mockLockBooking = (req: ILockBookingRequest) => {
  if (useMockWithCache && bookingDetailResponseWithCache[req.bookingId]) {
    const lock = bookingDetailResponseWithCache[req.bookingId].lock;
    if (lock && !lock.isActive) {
      lock.accountId = 'dummyAccountId';
      lock.accountName = 'dummyAccountName';
      lock.isActive = true;
      lock.updatedAt = new Date().toISOString();
    } else {
      throw new Error('Already locked!');
    }
  }
  return;
};

const mockuUnlockBooking = (req: IUnlockBookingRequest) => {
  if (useMockWithCache && bookingDetailResponseWithCache[req.bookingId]) {
    const lock = bookingDetailResponseWithCache[req.bookingId].lock;
    if (lock && lock.isActive) {
      lock.accountId = 'dummyAccountId';
      lock.accountName = 'dummyAccountName';
      lock.isActive = false;
      lock.updatedAt = new Date().toISOString();
    } else {
      throw new Error('Not locked!');
    }
  }
  return;
};

// api
export const searchBooking = async (req: ISearchBookingsRequest) => {
  if (useMock) {
    await sleep(500);
    return mockSearchBookingsResponseWithCache(req);
    // uncomment if use server side paging
    // return mockBookingSummaryWithServerPagination(req);
  }

  const res = await request({
    url: 'v1/search-bookings',
    method: 'post',
    data: req,
  });
  return res.data as ISearchBookingsResponse;
};

export const getBookingDetail = async (req: IGetBookingDetailRequest) => {
  if (useMock) {
    await sleep(500);
    return mockGetBookingDetailResponseWithCache(req);
  }

  const res = await request({
    url: 'v1/get-booking-detail',
    method: 'post',
    data: req,
  });
  return res.data as IGetBookingDetailResponse;
  // const tmp = res.data as IGetBookingDetailResponse;
  // tmp.issuingLogs = (tmp.issuingLogs || []).sort(
  //   (a, b) =>
  //     new Date(a.operatedAt).getTime() - new Date(b.operatedAt).getTime()
  // );
  // return tmp;
};

export const lockBooking = async (req: ILockBookingRequest) => {
  if (useMock) {
    await sleep(500);
    mockLockBooking(req);
    return;
  }

  await request({
    url: 'v1/lock-booking',
    method: 'post',
    data: req,
  });
  return;
};

export const unlockBooking = async (req: IUnlockBookingRequest) => {
  if (useMock) {
    await sleep(500);
    mockuUnlockBooking(req);
    return;
  }

  await request({
    url: 'v1/unlock-booking',
    method: 'post',
    data: req,
  });
  return;
};
